import { gsap, Expo, CSSPlugin } from 'gsap';
import { v4 as uuidv4 } from 'uuid';

gsap.registerPlugin(CSSPlugin);

/**
 * Opens an element
 *
 * @example
 * import {open} from '../helpers';
 *
 * open({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */
export const open = ({
  element,
  duration = 0.4,
  ease = 'easeOut',
  onComplete
}) => {
  gsap.set(element, { height: 'auto' });
  gsap.from(element, {
    height: 0,
    ease: Expo[ease],
    duration,
    onComplete: () => {
      onComplete && onComplete();
    }
  });
};

/**
 * Closes an element
 *
 * @example
 * import {close} from '../helpers';
 *
 * close({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */
export const close = ({
  element,
  duration = 0.4,
  ease = 'easeOut',
  onComplete
}) => {
  gsap.set(element, { height: element.offsetHeight });
  gsap.to(element, {
    height: 0,
    ease: Expo[ease],
    duration,
    onComplete: () => {
      onComplete && onComplete();
    }
  });
};

/**
 * Adds unique id to each element in the array
 * @param {Array} arr
 * @example
 * import {addUniqueIdsToArrayItems} from '../helpers';
 *
 * addUniqueIdsToArrayItems([{any: any}]);
 *
 */
export const addUniqueIdsToArrayItems = arr =>
  arr.map(item => ({ ...item, id: uuidv4() }));

export const getQueryStringValues = (...values) => {
  const queryString = document.location.search.replace('?', '');
  const queryParameters = queryString.split('&').map(parameter => {
    const splitParameter = parameter.split('=');
    return {
      key: splitParameter[0],
      value: splitParameter[1]
    };
  });
  if (!values) return queryParameters;
  const parameters = {};
  queryParameters
    .filter(({ key }) => values.includes(key))
    .forEach(parameter => {
      parameters[parameter.key] = parameter.value;
    });
  return parameters;
};

// /**
// * Checks if an element is focusable
// *
// * @param {Object} el - HTML element you want to check if it's focusable
// */
export const isFocusable = el => {
  if (
    el.tabIndex > 0 ||
    (el.tabIndex === 0 && el.getAttribute('tabIndex') !== null)
  ) {
    return true;
  }

  if (el.disabled) {
    return false;
  }

  switch (el.nodeName) {
    case 'A':
      return !!el.href && el.rel !== 'ignore';
    case 'INPUT':
      return el.type !== 'hidden' && el.type !== 'file';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return false;
  }
};

/**
 * Key code list object
 */
export const keyCode = {
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  SPACE: 32,
  PAGEUP: 33,
  PAGEDOWN: 34,
  END: 35,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40
};

// /**
// * Checks browser is running on a native mobile device
// */
export const isDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (
    /windows phone/i.test(userAgent) ||
    /android/i.test(userAgent) ||
    (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)
  ) {
    return true;
  }

  return false;
};

/**
 * Convers form data into a JSON object
 *
 * @param {Object} element - The form HTML element
 */
export const formDataToJson = formElement => {
  const formData = new FormData(formElement);
  const object = {};

  formData.forEach((value, key) => {
    if (!object.hasOwnProperty(key)) {
      object[key] = value;
      return;
    }
    if (!Array.isArray(object[key])) {
      object[key] = [object[key]];
    }
    object[key].push(value);
  });

  return object;
};

// /**
// * Debounce function
// * Delays the processing of the event
// */
export const debounce = (callback, wait) => {
  let timerId;
  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      callback(...args);
    }, wait);
  };
};

/**
 * Throttle function
 * Limits the rate at which a function can be called
 */
export const throttle = (callback, delay) => {
  let lastCallTime = 0;
  return (...args) => {
    const currentTime = new Date().getTime();
    if (currentTime - lastCallTime >= delay) {
      callback(...args);
      lastCallTime = currentTime;
    }
  };
};
